import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { WarrantTaxRatesResponse } from '@/api/settings'
import type { WarrantTaxRateVersion } from '@/domain/warrants'
import { apiFetch } from '~/api/client'
import { basisPointsOf, percentOf } from '~/state/tax'

export type { WarrantTaxRateVersion, WarrantTaxRatesResponse }

export const WARRANT_TAX_KEY = ['settings', 'warrant-tax'] as const

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export class WarrantTaxRateDraftError extends Error {}

export function useWarrantTaxRates(): UseQueryResult<WarrantTaxRatesResponse> {
  return useQuery({
    queryKey: WARRANT_TAX_KEY,
    queryFn: () => apiFetch<WarrantTaxRatesResponse>('/settings/warrant-tax'),
  })
}

/**
 * The whole history every time, for the reason the general rates are saved as
 * one: the version dated 2023 is what values an exercise in 2023, so an edit is
 * a statement about the past as much as about today.
 */
export function useSaveWarrantTaxRates(): UseMutationResult<
  { key: string; versions: WarrantTaxRateVersion[] },
  Error,
  readonly WarrantTaxRateVersion[]
> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (versions: readonly WarrantTaxRateVersion[]) =>
      apiFetch<{ key: string; versions: WarrantTaxRateVersion[] }>('/settings/warrant-tax', {
        method: 'PUT',
        body: JSON.stringify({ versions }),
      }),
    // The rate reaches the cash an exercise costs, which is three of the four
    // figures the Warrants page leads with.
    onSuccess: () => client.invalidateQueries(),
  })
}

/** A version as the boxes that restate it: one percentage per named regime. */
export interface WarrantTaxRateDraft {
  effectiveFrom: string
  exercisePercent: Readonly<Record<string, string>>
}

/**
 * Every regime the rows have to offer a box for: the ones this build can value
 * a grant under, and the ones only a stored version names.
 *
 * The second half is what keeps a save lossless. A household whose history
 * names a regime this build has never heard of would otherwise submit a version
 * with that rate quietly dropped, and grants written under it would be taxed at
 * nothing rather than reported as unpriced.
 */
export function regimesIn(
  offered: readonly string[],
  versions: readonly WarrantTaxRateVersion[],
): string[] {
  const seen = new Set(offered)
  for (const version of versions) {
    for (const regime of Object.keys(version.exerciseRateBp)) seen.add(regime)
  }
  return [...seen]
}

export function draftFromWarrantVersion(version: WarrantTaxRateVersion): WarrantTaxRateDraft {
  const exercisePercent: Record<string, string> = {}
  for (const [regime, bp] of Object.entries(version.exerciseRateBp)) {
    exercisePercent[regime] = percentOf(bp)
  }
  return { effectiveFrom: version.effectiveFrom, exercisePercent }
}

/**
 * A new version carries the rates of the one before it, for the reason the
 * general card's does: a blank row invites one regime to be typed and the rest
 * left empty, which is not a version with a gap in it but a jurisdiction that
 * charges nothing on exercise.
 */
export function nextWarrantDraftAfter(
  previous: WarrantTaxRateDraft | undefined,
  regimes: readonly string[],
): WarrantTaxRateDraft {
  if (previous === undefined) {
    return {
      effectiveFrom: '',
      exercisePercent: Object.fromEntries(regimes.map((regime) => [regime, ''])),
    }
  }
  return { ...previous, effectiveFrom: '' }
}

/** Why this version cannot be saved yet, in the words of whoever would refuse it. */
export function refuseWarrantTaxRateDraft(
  draft: WarrantTaxRateDraft,
  otherDates: readonly string[] = [],
): string | undefined {
  if (!ISO_DATE.test(draft.effectiveFrom)) return 'a date in the form YYYY-MM-DD is required'
  if (otherDates.includes(draft.effectiveFrom)) {
    return `two versions start on ${draft.effectiveFrom}, so the rates on that date are ambiguous`
  }

  for (const [regime, percent] of Object.entries(draft.exercisePercent)) {
    // A regime left blank is one the version says nothing about, which the
    // valuation reads as unknown rather than nil and reports as unknown. That
    // is a legitimate thing to state; an unreadable number in the box is not.
    if (percent.trim() === '') continue
    if (basisPointsOf(percent) === null) {
      return `the rate for ${regime} is a percentage between 0 and 100`
    }
  }

  return undefined
}

export function toWarrantTaxRateVersion(draft: WarrantTaxRateDraft): WarrantTaxRateVersion {
  const refusal = refuseWarrantTaxRateDraft(draft)
  if (refusal !== undefined) throw new WarrantTaxRateDraftError(refusal)

  const exerciseRateBp: Record<string, number> = {}
  for (const [regime, percent] of Object.entries(draft.exercisePercent)) {
    if (percent.trim() === '') continue
    const bp = basisPointsOf(percent)
    if (bp === null) {
      throw new WarrantTaxRateDraftError('a rate this form accepted is not a number after all')
    }
    exerciseRateBp[regime] = bp
  }

  return { effectiveFrom: draft.effectiveFrom, exerciseRateBp }
}

/**
 * The first refusal among the drafts, each checked against the dates of all the
 * others so that a clash is caught whichever of the two was typed second.
 */
export function refuseWarrantTaxRateDrafts(
  drafts: readonly WarrantTaxRateDraft[],
): string | undefined {
  return drafts
    .map((draft, index) =>
      refuseWarrantTaxRateDraft(
        draft,
        drafts.filter((_, other) => other !== index).map((other) => other.effectiveFrom),
      ),
    )
    .find((refusal) => refusal !== undefined)
}

export function sortWarrantDrafts(drafts: readonly WarrantTaxRateDraft[]): WarrantTaxRateDraft[] {
  return [...drafts].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom))
}
